import { useMutation, useQueryClient } from '@tanstack/react-query';

import { apiRequest } from '@/lib/api-client';

import { blOrderInvoicesKey } from './use-bl-order-invoices';
import { invoiceDetailKey } from './use-invoice-detail';
import type { InvoiceRow } from '../types';

export interface ConnectEasyBillMutationArgs {
  readonly invoiceId: string;
  readonly blOrderId?: string | undefined;
}

// The connect response is the invoice row plus whether the connect attached
// to a document that already existed in EasyBill or created a new one.
export type ConnectEasyBillResult = InvoiceRow & {
  readonly easybill_connect_mode: 'linked' | 'created';
};

// Connects a sell-side invoice to EasyBill — linking the document that
// already exists there when one matches, creating one otherwise. On success
// the invoice carries its easybill_document_id, so both the drawer (detail
// query) and the row listing (bl-order-invoices + bl-order-detail) re-render
// as Connected.
export function useConnectEasyBillMutation(args: ConnectEasyBillMutationArgs) {
  const qc = useQueryClient();
  return useMutation<ConnectEasyBillResult>({
    mutationFn: () =>
      apiRequest<ConnectEasyBillResult>(
        `/invoices/${encodeURIComponent(args.invoiceId)}/easybill`,
        {
          method: 'POST',
        },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: invoiceDetailKey(args.invoiceId) });
      if (args.blOrderId !== undefined) {
        void qc.invalidateQueries({ queryKey: blOrderInvoicesKey(args.blOrderId) });
      }
      void qc.invalidateQueries({ queryKey: ['bl-order-detail'] });
    },
  });
}
